import type {ReactNode} from 'react'
import {Link} from 'react-router'
import type {FunnelConfig} from '@core/products/funnel'
import {SignInOptions} from '@core/auth-ui/SignInOptions/SignInOptions'
import {useMagicLinkSent} from '@core/auth-ui/MagicLinkForm/MagicLinkForm'
import type {SignInCopy} from '@core/auth-ui/signInCopy'
import styles from './SignupGate.module.scss'

type SignupGateProps = {
    funnel: FunnelConfig
    authAction: string
    /** Same-origin relative path to return to once auth completes. See SignInOptions. */
    redirectTo?: string
    /** Which surface raised this gate — passed straight through to SignInOptions/MagicLinkForm
     *  so their auth_option_selected/magic_link_sent events join back to this gate's gate_hit.
     *  Match the trigger literal the caller already passes to gate_hit (e.g. 'pack-checkout',
     *  'swap', 'session') so the two events share the same value. */
    trigger?: string
    packSlug?: string
    /** ToS/consent line under the auth buttons (funnel spec §8: "By continuing you agree to the
     *  Terms and Privacy Policy" — acceptance by continuing, no checkbox, not tracked in
     *  analytics). Both required together; omitted by default so every existing caller across
     *  narcis/ovid/quies renders byte-identically unless it opts in with its OWN routes — this
     *  component is `@core/`, shared across all three products, and must never hardcode one
     *  product's `/terms`/`/privacy` path. */
    termsHref?: string
    privacyHref?: string
    googleIcon?: ReactNode
    /** No card chrome, title or message: the caller supplies its own frame and heading. */
    bare?: boolean
    /** The words of the gate, for a product that answers in more than one language. `title` and
     *  `message` stand in for the funnel's `gateTitle` and `gateMessage`; the rest are the sign-in
     *  controls'. All English from the funnel and the controls when omitted. */
    copy?: SignInCopy & {title?: string; message?: string}
}

export function SignupGate({funnel, authAction, redirectTo, trigger, packSlug, termsHref, privacyHref, googleIcon, bare, copy}: SignupGateProps) {
    // With a waiting view, the link sent replaces the gate's own heading: that view carries its own.
    const sent = useMagicLinkSent(!!copy?.waiting)
    const waitingView = !!copy?.waiting && sent
    return (
        <div className={bare ? styles.bare : styles.signupGate}>
            {!bare && !waitingView && (
                <>
                    <h3 className={styles.title}>{copy?.title ?? funnel.cta.gateTitle}</h3>
                    <p className={styles.message}>{copy?.message ?? funnel.cta.gateMessage}</p>
                </>
            )}

            <SignInOptions googleAuthAction={authAction} redirectTo={redirectTo} trigger={trigger} packSlug={packSlug} googleIcon={googleIcon} copy={copy} />

            {termsHref && privacyHref && (
                <p className={styles.tos}>
                    By continuing you agree to the <Link to={termsHref}>Terms</Link> and{' '}
                    <Link to={privacyHref}>Privacy Policy</Link>.
                </p>
            )}
        </div>
    )
}
